import React, { useMemo, useState } from 'react';
import { Collapse, Empty, Input, message, Spin, Switch } from 'antd';
import Tooltip from './Tooltip';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { getDocumentGroups, getDocuments, getDocumentPreviewUrl } from '../../requests/documentDatas';
import DuotoneLightIcon from './DuotoneLightIcon';
import { PRIMARY_DARK, getPrimaryDark } from '../../constants/constants';

// Libellé des groupes fixes ; les dossiers portent le leur, saisi dans Costing.
const GROUP_LABEL_KEYS = {
  opportunity: 'costing_documents_group_opportunity',
  offer: 'costing_documents_group_offer',
  no_folder: 'costing_documents_group_no_folder',
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

// Une ligne = un document. Le clic injecte, l'œil ouvre le fichier dans un
// onglet : l'URL n'est demandée qu'à ce moment-là, chaque signature ayant un
// coût côté serveur.
const DocumentRow = ({ doc, accentColor, onInject, onPreview }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const validity = formatDate(doc.date_validity);

  return (
    <div
      style={{ ...rowStyle, borderColor: hovered ? accentColor : 'rgba(11,34,57,0.12)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type='button'
        style={rowButtonStyle}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onInject(doc);
        }}
      >
        <DuotoneLightIcon name='file-pdf' style={{ fontSize: 16, color: accentColor, flexShrink: 0 }} />
        <span style={rowNameStyle}>{doc.name}</span>
        {validity && (
          <span style={{ ...rowValidityStyle, color: doc.is_valid ? 'rgba(11,34,57,0.45)' : '#cf1322' }}>
            {!doc.is_valid && (
              <DuotoneLightIcon name='triangle-exclamation' weight='solid' style={{ marginRight: 4 }} />
            )}
            {validity}
          </span>
        )}
      </button>
      <Tooltip title={t('costing_documents_preview')} placement='left' mouseEnterDelay={0.3}>
        <button
          type='button'
          style={eyeButtonStyle}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onPreview(doc);
          }}
        >
          <DuotoneLightIcon name='eye' style={{ fontSize: 12, color: PRIMARY_DARK }} />
        </button>
      </Tooltip>
    </div>
  );
};

// Contenu d'un groupe : chargé au dépliage seulement. La bibliothèque peut être
// volumineuse, on ne la tire pas entière à l'ouverture du panel.
const DocumentGroup = ({ groupKey, editorDocument, search, siteTypologies, accentColor, onInject }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery(
    ['CostingDocuments', editorDocument.type, editorDocument.id, groupKey, search, siteTypologies],
    () => getDocuments({ ...editorDocument, group: groupKey, search, siteTypologies }),
    { refetchOnWindowFocus: false, staleTime: 60000 }
  );

  // L'onglet est ouvert AVANT l'attente : ouvrir après un await sort du geste
  // utilisateur et se fait bloquer comme une pop-up. `opener` est coupé à la
  // main, le drapeau noopener de window.open empêchant de récupérer l'onglet
  // pour le rediriger.
  const handlePreview = async (doc) => {
    const tab = window.open('', '_blank');
    const url = await getDocumentPreviewUrl({ ...editorDocument, documentId: doc.id });
    if (!url) {
      tab?.close();
      message.error(t('costing_documents_preview_error'));
      return;
    }
    if (!tab) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    tab.opener = null;
    tab.location.href = url;
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 8 }}>
        <Spin size='small' />
      </div>
    );
  }

  if (!data?.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('costing_documents_empty')} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {data.map((doc) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          accentColor={accentColor}
          onInject={onInject}
          onPreview={handlePreview}
        />
      ))}
    </div>
  );
};

/**
 * Documents Costing injectables dans la box PDF sélectionnée : documents de
 * l'opportunité et de son offre, puis dossiers de la bibliothèque. Le périmètre
 * exact est décidé côté serveur (métier, agence, typologie, validité) ; le
 * panel ne fait qu'afficher ce qu'on lui donne.
 */
const CostingDocumentPicker = ({ onInject }) => {
  const { t } = useTranslation();
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const isTemplate = currentDocument?.type === 'template';
  // Une offre porte plusieurs opportunités : leurs documents sont réunis dans
  // un seul groupe, dont le libellé doit être au pluriel.
  const isOffer = currentDocument?.type === 'offer';
  const accentColor = getPrimaryDark(isTemplate);
  const [search, setSearch] = useState('');
  const [siteTypologies, setSiteTypologies] = useState(true);
  const [openGroups, setOpenGroups] = useState([]);

  const { data: groups, isLoading } = useQuery(
    ['CostingDocumentGroups', currentDocument?.type, currentDocument?.id],
    () => getDocumentGroups(currentDocument),
    { refetchOnWindowFocus: false, staleTime: 60000, enabled: !!currentDocument?.id }
  );

  const items = useMemo(() => {
    const groupLabel = (group) => {
      if (group.label) return group.label;
      if (group.kind === 'opportunity' && isOffer) return t('costing_documents_group_opportunities');
      return t(GROUP_LABEL_KEYS[group.kind] ?? group.kind);
    };

    return (groups ?? []).map((group) => ({
      key: group.key,
      label: (
        <span style={{ fontSize: 12, fontWeight: 600, color: accentColor }}>
          {groupLabel(group)}
        </span>
      ),
      children: (
        <DocumentGroup
          groupKey={group.key}
          editorDocument={currentDocument}
          search={search}
          siteTypologies={siteTypologies}
          accentColor={accentColor}
          onInject={onInject}
        />
      ),
    }));
  }, [groups, accentColor, currentDocument, search, siteTypologies, onInject, isOffer, t]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 12 }}>
        <Spin size='small' />
      </div>
    );
  }

  if (!items.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('costing_documents_empty')} />;
  }

  // Même garde que la banque d'images : le clic sur un entête de Collapse
  // remonte jusqu'à un ancêtre qui déselectionne la box, ce qui fermerait le
  // panel sous le curseur (cf. useOutsideAlerter). C'est stopPropagation qui
  // protège la sélection ; preventDefault est réservé au reste, car sur un champ
  // il empêcherait la prise de focus — la recherche deviendrait inutilisable.
  return (
    <div
      data-outside-ignore='true'
      onMouseDown={(event) => {
        event.stopPropagation();
        if (!event.target?.closest?.('input, textarea')) event.preventDefault();
      }}
      onClick={(event) => event.stopPropagation()}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <Input.Search
        allowClear
        size='small'
        placeholder={t('costing_documents_search')}
        onSearch={setSearch}
        onChange={(event) => {
          if (!event.target.value) setSearch('');
        }}
      />
      {/* Sans opportunité, aucune typologie de site à opposer aux documents. */}
      {!isTemplate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch size='small' checked={siteTypologies} onChange={setSiteTypologies} />
          <span style={{ fontSize: 11, color: PRIMARY_DARK }}>{t('costing_documents_typology_filter')}</span>
        </div>
      )}
      <Collapse ghost size='small' activeKey={openGroups} onChange={setOpenGroups} items={items} />
    </div>
  );
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  border: '1px solid rgba(11,34,57,0.12)',
  borderRadius: 6,
  background: '#fafafa',
  transition: 'border-color 0.15s ease',
};

const rowButtonStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 8px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
};

const rowNameStyle = {
  flex: 1,
  minWidth: 0,
  fontSize: 11,
  color: PRIMARY_DARK,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowValidityStyle = { fontSize: 10, flexShrink: 0, whiteSpace: 'nowrap' };

const eyeButtonStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '6px 8px',
};

export default CostingDocumentPicker;
